import ContactForm from "@/components/ContactForm";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { SITE } from "@/lib/site";
import { Phone, EnvelopeSimple, MapPin, ArrowRight, CalendarBlank } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function ContactPage() {
  useDocumentMeta();
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-32" data-testid="contact-page">
      <div className="overline">Contact</div>
      <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl tracking-tight leading-[0.95] mt-4 max-w-3xl">
        Booking questions, groups &amp; <em className="italic font-light">special requests.</em>
      </h1>
      <p className="mt-6 text-sa-ink2 text-lg max-w-2xl leading-relaxed">
        Straightforward stays are best booked directly through our booking engine — you&apos;ll see live availability and rates. Use the form below for group bookings, accessibility needs, long-stay enquiries, or anything else you&apos;d rather ask before booking.
      </p>

      {/* Primary CTA — Book direct via RMS */}
      <div
        className="mt-12 rounded-2xl bg-sa-ink text-sa-bg p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
        data-testid="contact-book-direct-card"
      >
        <div className="max-w-xl">
          <div className="flex items-center gap-2 text-sa-accent">
            <CalendarBlank size={22} weight="duotone" />
            <span className="overline text-sa-accent">Ready to book?</span>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight mt-3">
            Book direct — <em className="italic font-light">no middleman fees.</em>
          </h2>
          <p className="mt-3 text-sa-bg/80 text-[15px] leading-relaxed">
            Live availability, real-time rates, secure card checkout. Takes about 90 seconds.
          </p>
        </div>
        <Link
          to="/book"
          data-testid="contact-book-direct-btn"
          className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-sa-accent hover:bg-sa-accentHover text-white text-sm font-medium transition-colors group whitespace-nowrap"
        >
          Book a stay
          <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>

      <div className="mt-20 grid md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-7">
          <div className="mb-8">
            <div className="overline">Or send us a note</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-3">Any question we haven&apos;t answered?</h2>
            <p className="mt-3 text-sa-ink2 text-[15px] leading-relaxed max-w-lg">
              Group bookings, accessibility, long stays, pet questions, or anything you&apos;d like to check before booking. We typically reply within the hour, seven days a week.
            </p>
          </div>
          <ContactForm />
        </div>
        <aside className="md:col-span-5 md:sticky md:top-28 space-y-6">
          <div className="bg-white border border-sa-ink/10 rounded-2xl p-8">
            <div className="overline">Direct line</div>
            <ul className="mt-5 space-y-5 text-[15px]">
              <li className="flex items-center gap-3">
                <Phone size={20} weight="duotone" className="text-sa-accent" />
                <a href={`tel:${SITE.phone_intl}`} className="text-sa-ink hover:text-sa-accent">{SITE.phone}</a>
              </li>
              <li className="flex items-center gap-3">
                <EnvelopeSimple size={20} weight="duotone" className="text-sa-accent" />
                <a href={`mailto:${SITE.email}`} className="text-sa-ink hover:text-sa-accent">{SITE.email}</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={20} weight="duotone" className="mt-0.5 text-sa-accent" />
                <span className="text-sa-ink">{SITE.address}</span>
              </li>
            </ul>
          </div>
          <div className="bg-sa-ink text-sa-bg rounded-2xl p-8">
            <div className="overline text-sa-accent">Opening hours</div>
            <ul className="mt-5 space-y-2 text-sm text-sa-bg/85">
              <li className="flex justify-between"><span>Mon – Fri</span><span>8am – 8pm</span></li>
              <li className="flex justify-between"><span>Sat – Sun</span><span>7am – 9pm</span></li>
              <li className="flex justify-between"><span>Public holidays</span><span>9am – 6pm</span></li>
            </ul>
          </div>
        </aside>
      </div>
    </section>
  );
}
